import { CustomValidationRenderer } from '@rightship-tech/common-ui/client-validation-system/custom-validation-renderer';
import { autoinject, computedFrom, observable } from "aurelia-framework";
import { ValidationController, ValidationControllerFactory, ValidationRules } from 'aurelia-validation';
import { RightShipInspectionDetailsModel, RightShipInspectionStatusEnum } from '../../../models/rightship-inspections.models';
import { RightshipInspectionService } from '../../../services/rightship-inspection.service';
import { NotificationClientService, NotificationType } from '@rightship-tech/common-ui/services/basic/notification-client.service';
import { ValidationsClientService } from "@rightship-tech/common-ui/services/basic/validations-client.service";
import { RsInspectionRouteConstants } from '../../../constants/route.constants';
import { GoogleAnalyticsService } from '@rightship-tech/common-ui/services/analytics/google-analytics.service';
import { Router } from 'aurelia-router';
import { RsInspectionGoogleAnalyticsActionName } from '../../../constants/google-analytics.constants';
import { TitleConst } from '../../../constants/title.constaints';
import { LookupValue } from '@rightship-tech/common-ui/models/lookup.models';

@autoinject
export class CancelInspection {
    public otherReason: string = "";
    public inspectionModel: RightShipInspectionDetailsModel;
    public messageCenterUrl: string;
    public readonly title: string = TitleConst.CancelInspection;

    private _encryptedData: string;
    private _validator: ValidationController;

    @observable()
    public reason: LookupValue = null;

    @computedFrom("reason.name")
    get isOtherReason(): boolean {
        return this.reason?.name?.toLowerCase() === 'other';
    }

    constructor(validationControllerFactory: ValidationControllerFactory, private rightshipInspectionService: RightshipInspectionService,
                private googleAnalyticsService: GoogleAnalyticsService,
                private router: Router, private notificationClientService: NotificationClientService, private validationsClientService: ValidationsClientService
    ) {
        this._validator = validationControllerFactory.createForCurrentScope();
        this._validator.addRenderer(new CustomValidationRenderer());
    }

    async activate(params: { encryptedData: string }) {
        this._encryptedData = decodeURIComponent(params.encryptedData);
        this.inspectionModel = await this.rightshipInspectionService.getRightShipInspectionDetails(this._encryptedData);
        this.messageCenterUrl = `/inspection/${params.encryptedData}`;

        if (this.inspectionModel.statusId == RightShipInspectionStatusEnum.Cancelled
            || this.inspectionModel.statusId == RightShipInspectionStatusEnum.Acceptable
            || this.inspectionModel.statusId == RightShipInspectionStatusEnum.Unacceptable
        ) {
            this.router.navigateToRoute(RsInspectionRouteConstants.CloseRequest, { isDeclined: false, encryptedData: encodeURIComponent(this._encryptedData), inspectionModel: this.inspectionModel });
            return;
        }
    }

    async attached() {
        this.setUpValidation();
    }

    setUpValidation() {
        this.getRules();
    }

    getRules(){
        ValidationRules
            .ensure<CancelInspection, LookupValue>(x => x.reason)
            .required()
            .withMessage("Please enter reason for canceling inspection")

            .ensure(x => x.otherReason)
            .required()
            .withMessage("Please enter detail reason for canceling inspection")
            .when(x => x.isOtherReason)

            .on(this);
    }

    reasonChanged(){
        if (this._validator?.errors) this._validator.reset();
    }

    async save() {
        const validator = await this._validator.validate();
        if (validator.valid) {
            try {
                const response = await this.rightshipInspectionService.cancelInspection({
                    reasonOption: this.reason.name,
                    reason: this.isOtherReason ? this.otherReason : this.reason.name,
                    encryptedData: this._encryptedData,
                });

                if (response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                } else {
                    if (response.successMessage) {

                        //Send GA4 event
                        if (this.inspectionModel.hasAssignedInspector) {
                            this.googleAnalyticsService.sendCustomGA4EventMultiAnonymously(RsInspectionGoogleAnalyticsActionName.CancelInspectionAfterAssigned, {}, this.inspectionModel.emailIdentifier);
                        }
                        else {
                            this.googleAnalyticsService.sendCustomGA4EventMultiAnonymously(RsInspectionGoogleAnalyticsActionName.CancelInspectionBeforeAssigned, {}, this.inspectionModel.emailIdentifier);
                        }
                        this.router.navigateToRoute(RsInspectionRouteConstants.SubmittedCancellation, {
                            vesselName: this.inspectionModel.vesselDetails.vesselName,
                            vesselImo: this.inspectionModel.vesselDetails.vesselImo,
                            portName: this.inspectionModel.portAndOperationalDetails.port,
                            country: this.inspectionModel.portAndOperationalDetails.country,
                            requestedInspectionDate: this.inspectionModel.portAndOperationalDetails.requestedInspectionDate,
                            reason: response.reason,
                            rsInspectionId: this.inspectionModel.rightShipInspectionId,
                            encryptedData: encodeURIComponent(this._encryptedData)
                        });
                        this.notificationClientService.show("Cancel inspection succeed", response.successMessage, NotificationType.Success);
                    }
                }
            } catch (error) {
                this.notificationClientService.show("Cancel inspection failed", "Error occurred while canceling inspection", NotificationType.Error);
            }
        }
    }
}